import styled from 'styled-components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { TextField, Button } from '@avangard/ui/core';

import { FormStyled } from '@modules/layout/styled';
import { ExtendedFormik, Form } from '@modules/layout/organisms';

type CourseCreateFormProps<T> = Omit<FormProps<T>, 'FormWrapComponent'> & {
    onCancel: () => void;
    submitText?: string;
};

const StyledForm = styled(Form)`
    width: 100%;

    > * {
        &:not(:last-of-type) {
            margin-bottom: 26px;
        }
    }
`;

const FormRowSubmit = styled(FormStyled.FormRowSubmit)`
    justify-content: flex-end;
`;

const CourseForm = <T extends Record<string, any>>(props: CourseCreateFormProps<T>) => {
    const { initialValues, submitText, onSubmit, onCancel } = props;

    const { t } = useTranslation(['course']);

    return (
        <ExtendedFormik<T>
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={yup.object({
                name: yup.string().required(t('errors:validations.required') ?? ''),
            })}
            initialValues={initialValues}
            onSubmit={onSubmit}
        >
            {formikProps => {
                const { values, errors, handleChange, handleBlur, isSubmitting } = formikProps;

                return (
                    <StyledForm>
                        <TextField
                            required
                            fullWidth
                            id='name'
                            label={t('course:form.labels.course_name')}
                            placeholder={t('course:form.placeholder.typing_course_name') ?? ''}
                            value={values.name}
                            error={!!errors.name}
                            helperText={errors.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <TextField
                            fullWidth
                            id='sort'
                            type='number'
                            label={t('course:form.labels.course_sort')}
                            placeholder={t('course:form.placeholder.typing_course_sort') ?? ''}
                            value={values.sort}
                            error={!!errors.sort}
                            helperText={errors.sort}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <FormRowSubmit>
                            <Button variant='outlined' disabled={isSubmitting} onClick={onCancel}>
                                {t('common:actions.cancel')}
                            </Button>

                            <Button type='submit' loading={isSubmitting}>
                                {submitText ?? t('common:actions.add')}
                            </Button>
                        </FormRowSubmit>
                    </StyledForm>
                );
            }}
        </ExtendedFormik>
    );
};

export { CourseForm };
